// src/pages/buyer/RfqPage.jsx
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Calendar, MapPin, X, ChevronDown, ChevronUp } from 'lucide-react'
import { rfqApi } from '../../api/services'
import { Button, Card, CardHeader, CardBody, StatusBadge, PageSpinner, Empty, Badge } from '../../components/common/ui'
import toast from 'react-hot-toast'

const FABRIC_TYPES = ['Cotton', 'Silk', 'Linen', 'Wool', 'Polyester', 'Rayon', 'Denim', 'Chiffon', 'Georgette', 'Satin', 'Velvet', 'Organza', 'Khadi', 'Jute']
const WEAVE_TYPES = ['Plain', 'Twill', 'Satin', 'Dobby', 'Jacquard', 'Basket', 'Herringbone', 'Oxford']
const INDIAN_STATES = ['Andhra Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal']

const INIT = {
  title: '', description: '', fabric_type: '', quantity_metres: '', target_price_per_metre: '',
  gsm_range: '', colour_preference: '', weave_type: '', deadline: '',
  contact_name: '', contact_phone: '', delivery_state: '',
}

const STATUS_TABS = [
  { key: 'all',     label: 'All' },
  { key: 'open',    label: 'Open' },
  { key: 'awarded', label: 'Awarded' },
  { key: 'closed',  label: 'Closed' },
]

export default function RfqPage() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(INIT)
  const [statusFilter, setStatusFilter] = useState('all')
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const { data, isLoading } = useQuery({
    queryKey: ['rfqs'],
    queryFn: () => rfqApi.buyerList(),
  })

  const create = useMutation({
    mutationFn: () => rfqApi.create(form),
    onSuccess: () => {
      toast.success('RFQ posted!')
      qc.invalidateQueries({ queryKey: ['rfqs'] })
      setShowForm(false)
      setForm(INIT)
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed to post RFQ'),
  })

  if (isLoading) return <PageSpinner />
  const allRfqs = data?.data?.data?.data || data?.data?.data || []

  // Filter by status
  const rfqs = statusFilter === 'all' ? allRfqs : allRfqs.filter(r => r.status === statusFilter)

  // KPIs
  const totalRfqs = allRfqs.length
  const openCount = allRfqs.filter(r => r.status === 'open').length
  const totalQuotes = allRfqs.reduce((s, r) => s + (r.quotes_count || 0), 0)
  const awardedCount = allRfqs.filter(r => r.status === 'awarded').length

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold">Request for Quotes</h1>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? 'Close' : 'New RFQ'}
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total RFQs', value: totalRfqs, color: 'text-gray-900' },
          { label: 'Active', value: openCount, color: 'text-green-600' },
          { label: 'Quotes Received', value: totalQuotes, color: 'text-blue-600' },
          { label: 'Awarded', value: awardedCount, color: 'text-purple-600' },
        ].map(k => (
          <div key={k.label} className="bg-white border border-border rounded-2xl p-4 text-center">
            <p className={`text-2xl font-extrabold ${k.color}`}>{k.value}</p>
            <p className="text-xs text-muted mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Create RFQ Form */}
      {showForm && (
        <Card className="mb-6">
          <CardHeader><span className="font-bold text-sm">Post New RFQ</span></CardHeader>
          <CardBody className="space-y-4">
            <input className="input w-full" placeholder="Title — e.g. 'Need 500m pure cotton shirting fabric' *" value={form.title} onChange={set('title')} />
            <textarea className="input w-full h-24" placeholder="Describe your requirement in detail — quality expectations, certifications needed, intended use... *" value={form.description} onChange={set('description')} />

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-muted mb-1 block">Fabric Type</label>
                <select className="input w-full" value={form.fabric_type} onChange={set('fabric_type')}>
                  <option value="">Any fabric</option>
                  {FABRIC_TYPES.map(f => <option key={f} value={f.toLowerCase()}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-muted mb-1 block">Quantity (metres) *</label>
                <input className="input w-full" type="number" placeholder="e.g. 500" value={form.quantity_metres} onChange={set('quantity_metres')} />
              </div>
              <div>
                <label className="text-xs font-bold text-muted mb-1 block">Target Price/m</label>
                <input className="input w-full" type="number" placeholder="e.g. 120" value={form.target_price_per_metre} onChange={set('target_price_per_metre')} />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-muted mb-1 block">GSM Range</label>
                <input className="input w-full" placeholder="e.g. 140-180" value={form.gsm_range} onChange={set('gsm_range')} />
              </div>
              <div>
                <label className="text-xs font-bold text-muted mb-1 block">Colour Preference</label>
                <input className="input w-full" placeholder="e.g. Navy blue, White" value={form.colour_preference} onChange={set('colour_preference')} />
              </div>
              <div>
                <label className="text-xs font-bold text-muted mb-1 block">Weave Type</label>
                <select className="input w-full" value={form.weave_type} onChange={set('weave_type')}>
                  <option value="">Any weave</option>
                  {WEAVE_TYPES.map(w => <option key={w} value={w.toLowerCase()}>{w}</option>)}
                </select>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-muted mb-1 block">Contact Name *</label>
                <input className="input w-full" placeholder="Your name" value={form.contact_name} onChange={set('contact_name')} />
              </div>
              <div>
                <label className="text-xs font-bold text-muted mb-1 block">Phone *</label>
                <input className="input w-full" placeholder="10-digit mobile" value={form.contact_phone} onChange={set('contact_phone')} />
              </div>
              <div>
                <label className="text-xs font-bold text-muted mb-1 block">Delivery State</label>
                <select className="input w-full" value={form.delivery_state} onChange={set('delivery_state')}>
                  <option value="">Any location</option>
                  {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-muted mb-1 block">Deadline</label>
                <input className="input w-full" type="date" value={form.deadline} onChange={set('deadline')} />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button loading={create.isPending} onClick={() => create.mutate()} disabled={!form.title || !form.description || !form.quantity_metres || !form.contact_name || !form.contact_phone}>
                Post RFQ
              </Button>
              <Button variant="ghost" onClick={() => { setShowForm(false); setForm(INIT) }}>Cancel</Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Status Tabs */}
      <div className="flex gap-1 mb-4 bg-surface rounded-xl p-1 w-fit">
        {STATUS_TABS.map(t => {
          const count = t.key === 'all' ? totalRfqs : allRfqs.filter(r => r.status === t.key).length
          return (
            <button key={t.key}
              onClick={() => setStatusFilter(t.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${statusFilter === t.key ? 'bg-white shadow-sm text-gray-900' : 'text-muted hover:text-gray-700'}`}>
              {t.label} {count > 0 && <span className="ml-1 text-[10px] opacity-60">({count})</span>}
            </button>
          )
        })}
      </div>

      {/* RFQ List */}
      {rfqs.length === 0 && !showForm && (
        <Empty icon="📋" title="No RFQs yet"
          description="Post an RFQ to get competitive quotes from verified suppliers across India."
          action={<Button onClick={() => setShowForm(true)}>Post Your First RFQ</Button>} />
      )}

      <div className="space-y-3">
        {rfqs.map(rfq => {
          const daysLeft = rfq.deadline ? Math.ceil((new Date(rfq.deadline) - new Date()) / 86400000) : null
          return (
            <Card key={rfq.id} className="hover:shadow-md transition-shadow">
              <CardBody>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm truncate">{rfq.title}</span>
                      <StatusBadge status={rfq.status} />
                      {rfq.quotes_count > 0 && (
                        <Badge className="bg-blue-50 text-blue-700 text-[10px]">{rfq.quotes_count} quote{rfq.quotes_count > 1 ? 's' : ''}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted mt-1">{rfq.rfq_number}</p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap text-xs text-gray-600">
                      <span>{rfq.quantity_metres}m</span>
                      {rfq.fabric_type && <span className="bg-gray-100 px-2 py-0.5 rounded-full">{rfq.fabric_type}</span>}
                      {rfq.target_price_per_metre && <span>Target: ₹{rfq.target_price_per_metre}/m</span>}
                      {daysLeft !== null && (
                        <span className={`flex items-center gap-1 ${daysLeft < 0 ? 'text-red-500' : daysLeft <= 3 ? 'text-amber-600' : 'text-gray-500'}`}>
                          <Calendar size={11} />
                          {daysLeft < 0 ? 'Expired' : daysLeft === 0 ? 'Expires today' : `${daysLeft}d left`}
                        </span>
                      )}
                      {rfq.delivery_state && (
                        <span className="flex items-center gap-1 text-gray-500"><MapPin size={11} /> {rfq.delivery_state}</span>
                      )}
                    </div>
                    {rfq.awarded_quote && (
                      <p className="text-xs text-green-700 mt-2 font-medium">
                        Awarded to {rfq.awarded_quote.supplier?.business_name || rfq.awarded_quote.supplier?.name} at ₹{rfq.awarded_quote.price_per_metre}/m
                      </p>
                    )}
                  </div>
                  <Link to={`/buyer/rfq/${rfq.id}`}>
                    <Button variant="secondary" size="sm">
                      {rfq.status === 'open' && rfq.quotes_count > 0 ? 'Review Quotes' : 'View Details'} →
                    </Button>
                  </Link>
                </div>
              </CardBody>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
